import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Collapse, Skeleton, Stack, Typography } from '@mui/material';
import InfoAlert from 'src/components/InfoAlert';
import { useUnmatchedFilesPolicy, useUnmatchedFilesSummary } from 'src/hooks/wdac';
import UnmatchedFilesCatalogCard from './UnmatchedFilesCatalogCard';
import {
  UNMATCHED_FILE_STATUS,
  getUnmatchedFileStatusColor,
  getUnmatchedFileStatusLabel,
} from 'src/utils/enums/unmatchedFileStatus';
import {
  UNMATCHED_FILE_LAST_SEEN_RANGE,
  UNMATCHED_FILE_TRUST_SIGNAL,
  UNMATCHED_FILE_TRUST_SIGNAL_OPTIONS,
  UNMATCHED_FILE_TRUST_SIGNAL_COUNT_LABEL,
  UNMATCHED_FILE_GROUP_BY,
  TRUST_SIGNAL_INFO,
  getUnmatchedFileLastSeenRangeLabel,
} from './constants';
import { getLastSeenRangeDates } from './sharedQueryHelper';
import {
  AllClearCallout,
  CardTitle,
  ChartTooltipBox,
  DETECTED_HEADING_SX,
  DetectedCallout,
  EmptyChartState,
  LegendPill,
  OVERVIEW_CARD_SX as CARD_SX,
  StepRow,
} from 'src/features/charts/OverviewCard';
import { formatCount } from 'src/utils/formatNumber.util';
import { DATE_RANGE_COLORS } from 'src/features/charts/palette';

const TRUST_SIGNAL_TO_GROUP_BY = {
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedSigners]: UNMATCHED_FILE_GROUP_BY.TbsHash,
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedPublisherNames]: UNMATCHED_FILE_GROUP_BY.PublisherName,
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedIssuers]: UNMATCHED_FILE_GROUP_BY.PublisherName,
  [UNMATCHED_FILE_TRUST_SIGNAL.SignedByUnknownCert]: UNMATCHED_FILE_GROUP_BY.TbsHash,
};

const TRUST_SIGNAL_TO_TABLE = {
  [UNMATCHED_FILE_TRUST_SIGNAL.MatchedApps]: 'matchedApps',
  [UNMATCHED_FILE_TRUST_SIGNAL.Unsigned]: 'files',
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedSigners]: 'signers',
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedPublisherNames]: 'signers',
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedIssuers]: 'signers',
  [UNMATCHED_FILE_TRUST_SIGNAL.SignedByUnknownCert]: 'signers',
};

const TRUST_SIGNAL_PRIORITY = [
  UNMATCHED_FILE_TRUST_SIGNAL.MatchedApps,
  UNMATCHED_FILE_TRUST_SIGNAL.TrustedSigners,
  UNMATCHED_FILE_TRUST_SIGNAL.TrustedPublisherNames,
  UNMATCHED_FILE_TRUST_SIGNAL.TrustedIssuers,
  UNMATCHED_FILE_TRUST_SIGNAL.SignedByUnknownCert,
  UNMATCHED_FILE_TRUST_SIGNAL.Unsigned,
];

const LAST_SEEN_RANGES = [
  UNMATCHED_FILE_LAST_SEEN_RANGE.Last30Days,
  UNMATCHED_FILE_LAST_SEEN_RANGE.Days30To60,
  UNMATCHED_FILE_LAST_SEEN_RANGE.Days60Plus,
];

const LAST_SEEN_COLORS = {
  [UNMATCHED_FILE_LAST_SEEN_RANGE.Last30Days]: DATE_RANGE_COLORS[0],
  [UNMATCHED_FILE_LAST_SEEN_RANGE.Days30To60]: DATE_RANGE_COLORS[1],
  [UNMATCHED_FILE_LAST_SEEN_RANGE.Days60Plus]: DATE_RANGE_COLORS[2],
};

const TRUST_SIGNAL_CONFIG = UNMATCHED_FILE_TRUST_SIGNAL_OPTIONS.filter(option => option.value).map(
  option => ({
    key: option.value,
    label: UNMATCHED_FILE_TRUST_SIGNAL_COUNT_LABEL[option.value] || option.label,
    countKey: option.value,
  }),
);

const hasData = data => data.some(item => Number(item.value || 0) > 0);
const getRangeCount = (rangeCounts, range) =>
  (rangeCounts || []).find(item => item.range === range)?.count || 0;
const getFirstNonZeroTrustSignal = counts =>
  TRUST_SIGNAL_PRIORITY.find(key => Number(counts?.[key] || 0) > 0) ||
  UNMATCHED_FILE_TRUST_SIGNAL.All;

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }
  const item = payload[0]?.payload;
  return (
    <ChartTooltipBox>
      {item?.label}: {formatCount(item?.value)}
    </ChartTooltipBox>
  );
};

const GET_STARTED_STEPS = [
  {
    title: 'Filter down to files of interest',
    detail: 'Pick a trust status, last audit/block range, or trust signal from the charts.',
  },
  {
    title: 'Set trust for files based on collected data',
    detail: 'Trust or ignore files from the Unmatched Files or Code Signers table.',
  },
  {
    title: 'Create a catalog or policy for all trusted files',
    detail: 'Generate a security catalog to cover everything you trusted.',
  },
];

const GetStartedCard = ({
  onTrustedClick,
  onUnknownClick,
  onPendingClick,
  showUnknown,
  showTrusted,
  showPending,
  unknownCount,
  trustedRecentCount,
  pendingCatalogCount,
}) => (
  <Stack gap={1.25} sx={{ ...CARD_SX, flex: '1 1 0', minWidth: 260 }}>
    <CardTitle title="Get Started" subtitle="Work your unmatched files down in three steps." />
    <Stack gap={1.1} flex={1}>
      {GET_STARTED_STEPS.map((step, idx) => (
        <StepRow key={step.title} index={idx + 1} title={step.title} detail={step.detail} />
      ))}
    </Stack>
    <Stack gap={0.6}>
      <Typography sx={DETECTED_HEADING_SX}>Detected in your environment</Typography>
      {!showUnknown && !showTrusted && !showPending ? <AllClearCallout /> : null}
      {showUnknown ? (
        <DetectedCallout
          icon="mdi:shield-alert-outline"
          accent="#E0A23C"
          title={`${formatCount(unknownCount)} unknown files seen in the last 30 days`}
          subtitle="Start here — these files block your policy from being deployed until reviewed."
          onClick={onUnknownClick}
        />
      ) : null}
      {showPending ? (
        <DetectedCallout
          icon="mdi:shield-alert-outline"
          accent="#E0A23C"
          title={`${formatCount(
            pendingCatalogCount,
          )} updated files not represented in a security catalog yet`}
          subtitle="Your policy must be regenerated and then redeployed to incorporate your changes."
          onClick={onPendingClick}
        />
      ) : null}
      {showTrusted ? (
        <DetectedCallout
          icon="mdi:shield-alert-outline"
          accent="#E0A23C"
          title={`${formatCount(trustedRecentCount)} trusted files seen in the last 30 days`}
          subtitle="Monitor events to determine if catalogs still need to be deployed."
          onClick={onTrustedClick}
        />
      ) : null}
    </Stack>
  </Stack>
);

const TrustStatusPie = ({ data, selectedStatus, onStatusClick }) => (
  <Stack gap={0.75} sx={{ width: { xs: '100%', md: 190 }, flexShrink: 0 }}>
    <CardTitle title="Trust Status" subtitle="All files · click a slice to filter" size="sm" />
    {hasData(data) ? (
      <>
        <Box sx={{ width: 118, height: 118, alignSelf: 'center' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                innerRadius={32}
                outerRadius={54}
                paddingAngle={2}
                cursor="pointer"
                onClick={entry => onStatusClick(entry?.status)}>
                {data.map(item => {
                  const selected = selectedStatus === item.status;
                  return (
                    <Cell
                      key={item.status}
                      fill={item.color}
                      stroke={selected ? '#7DFF92' : '#454545'}
                      strokeWidth={selected ? 3 : 1}
                      opacity={selectedStatus && !selected ? 0.45 : 1}
                    />
                  );
                })}
              </Pie>
              <RechartsTooltip content={<ChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="center">
          {data.map(item => (
            <LegendPill
              key={item.status}
              color={item.color}
              label={item.label}
              value={item.value}
              selected={selectedStatus === item.status}
              onClick={() => onStatusClick(item.status)}
            />
          ))}
        </Stack>
      </>
    ) : (
      <EmptyChartState height={142} />
    )}
  </Stack>
);

const LastSeenBars = ({ statusLabel, data, selectedRange, onRangeClick }) => (
  <Stack gap={0.75} flex={1} minWidth={220}>
    <CardTitle
      title="Last Audit / Block Event"
      subtitle={`${statusLabel} files · click a bar to filter`}
      size="sm"
    />
    {hasData(data) ? (
      <>
        <Box sx={{ width: '100%', height: 108 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 4, right: 18, left: 0, bottom: 0 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.16)"
                horizontal={false}
              />
              <XAxis
                type="number"
                allowDecimals={false}
                stroke="#D1D5DB"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 10, fill: '#D1D5DB' }}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={8}
                tick={false}
                axisLine={false}
                tickLine={false}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer" onClick={onRangeClick}>
                {data.map(item => {
                  const selected = selectedRange === item.range;
                  return (
                    <Cell
                      key={item.range}
                      fill={LAST_SEEN_COLORS[item.range]}
                      stroke={selected ? '#7DFF92' : 'transparent'}
                      strokeWidth={selected ? 2 : 0}
                      opacity={selectedRange && !selected ? 0.45 : 1}
                    />
                  );
                })}
              </Bar>
              <RechartsTooltip
                content={<ChartTooltip />}
                cursor={{ fill: 'rgba(255,255,255,0.06)' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="center">
          {data.map(item => (
            <LegendPill
              key={item.range}
              color={LAST_SEEN_COLORS[item.range]}
              label={item.label}
              value={item.value}
              selected={selectedRange === item.range}
              onClick={() => onRangeClick(item)}
            />
          ))}
        </Stack>
      </>
    ) : (
      <EmptyChartState
        height={140}
        message={`No last audit/block data for ${statusLabel} files.`}
      />
    )}
  </Stack>
);

const TrustSignalsCard = ({ counts, selectedTrustSignal, onTrustSignalClick }) => (
  <Stack gap={1} sx={{ ...CARD_SX, width: '100%' }}>
    <CardTitle
      title="Unmatched File Trust Signals"
      subtitle="For each unmatched file, we determine if the exact file is in a cataloged application, then sort each signer into a relevant trust signal group."
    />
    <Stack
      direction="row"
      alignItems="stretch"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1}>
      {TRUST_SIGNAL_CONFIG.map(signal => {
        const selected = selectedTrustSignal === signal.key;
        return (
          <Stack
            key={signal.key}
            alignItems="center"
            justifyContent="center"
            gap={0.5}
            onClick={() => onTrustSignalClick(signal.key)}
            sx={{
              flex: '1 1 0',
              minWidth: 120,
              cursor: 'pointer',
              borderRadius: '8px',
              border: selected ? '1px solid #7DFF92' : '1px solid transparent',
              backgroundColor: selected ? 'rgba(125, 255, 146, 0.08)' : 'rgba(255,255,255,0.03)',
              px: 1,
              py: 1.25,
              '&:hover': { borderColor: '#87C8E0' },
            }}>
            <Typography
              sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.2 }}>
              {formatCount(counts[signal.countKey])}
            </Typography>
            <Typography
              align="center"
              sx={{ color: '#D1D5DB', fontSize: '0.6rem', lineHeight: 1.2 }}>
              {signal.label}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  </Stack>
);

const TrustSignalAlert = ({ signal }) => {
  const info = TRUST_SIGNAL_INFO[signal];
  if (!info) {
    return null;
  }
  return (
    <InfoAlert sx={{ py: 0.75 }}>
      <Typography sx={{ color: 'inherit', fontSize: '0.74rem', lineHeight: 1.4 }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {info.title}:
        </Box>{' '}
        {info.description}
        {info.groupingNote ? (
          <Box component="span" sx={{ fontStyle: 'italic' }}>
            {' '}
            {info.groupingNote}
          </Box>
        ) : null}
      </Typography>
    </InfoAlert>
  );
};

const SectionSkeleton = () => (
  <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems="stretch">
    <Skeleton
      variant="rounded"
      height={300}
      sx={{ bgcolor: '#454545', flex: '1 1 0', minWidth: 260 }}
    />
    <Stack gap={1.5} sx={{ flex: '1 1 0', minWidth: 320 }}>
      <Skeleton variant="rounded" height={92} sx={{ bgcolor: '#454545' }} />
      <Skeleton variant="rounded" sx={{ bgcolor: '#454545', flex: '1 1 auto', minHeight: 200 }} />
    </Stack>
  </Stack>
);

const buildRangeData = rangeCounts => {
  const byRange = new Map((rangeCounts || []).map(item => [item.range, item.count]));
  return LAST_SEEN_RANGES.map(range => ({
    range,
    label: getUnmatchedFileLastSeenRangeLabel(range),
    value: byRange.get(range) || 0,
  }));
};

const UnmatchedFilesCharts = ({
  filters,
  onFiltersChange,
  onGroupByChange,
  onActiveTableChange,
  initialFocusPending = false,
  onInitialFocusComplete,
}) => {
  const applyUnknownStarterFilters = useCallback(
    counts => {
      const signal = getFirstNonZeroTrustSignal(counts);

      onFiltersChange({
        ...filters,
        trustStatus: UNMATCHED_FILE_STATUS.Unknown,
        lastSeenRange: UNMATCHED_FILE_LAST_SEEN_RANGE.Last30Days,
        trustSignal: signal,
      });
      if (onGroupByChange && TRUST_SIGNAL_TO_GROUP_BY[signal]) {
        onGroupByChange(TRUST_SIGNAL_TO_GROUP_BY[signal]);
      }
      if (onActiveTableChange) {
        onActiveTableChange(TRUST_SIGNAL_TO_TABLE[signal] || 'files');
      }
    },
    [filters, onFiltersChange, onGroupByChange, onActiveTableChange],
  );

  // The overview is environment-wide: status counts and the per-status last-seen bars never vary
  // with the table filters. The one exception is the trust-signal counts, which reflect the
  // selected last audit/block range (handled by passing the range to the summary query).
  const summaryApiQuery = useMemo(() => {
    const params = {};
    const { startDate, endDate } = getLastSeenRangeDates(filters.lastSeenRange);
    if (startDate) {
      params.lastSeenStartDate = startDate.toISOString();
    }
    if (endDate) {
      params.lastSeenEndDate = endDate.toISOString();
    }
    return params;
  }, [filters.lastSeenRange]);
  const summaryQuery = useUnmatchedFilesSummary({ apiQuery: summaryApiQuery });
  const starterSummaryApiQuery = useMemo(() => {
    const params = {};
    const { startDate, endDate } = getLastSeenRangeDates(UNMATCHED_FILE_LAST_SEEN_RANGE.Last30Days);
    if (startDate) {
      params.lastSeenStartDate = startDate.toISOString();
    }
    if (endDate) {
      params.lastSeenEndDate = endDate.toISOString();
    }
    return params;
  }, []);
  const starterSummaryQuery = useUnmatchedFilesSummary({ apiQuery: starterSummaryApiQuery });
  const policyStateQuery = useUnmatchedFilesPolicy();
  const unrepresentedFileCount = policyStateQuery.data?.unrepresentedFileCount ?? 0;
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);

  useEffect(() => {
    if (!initialFocusPending || (!starterSummaryQuery.data && !starterSummaryQuery.isError)) {
      return;
    }

    if (starterSummaryQuery.data) {
      applyUnknownStarterFilters(starterSummaryQuery.data.trustSignalCounts || {});
    }
    if (onInitialFocusComplete) {
      onInitialFocusComplete();
    }
  }, [
    applyUnknownStarterFilters,
    initialFocusPending,
    onInitialFocusComplete,
    starterSummaryQuery.data,
    starterSummaryQuery.isError,
  ]);

  // The status pie / last-seen bars are environment-wide and don't reflect a device tag, so they
  // unhighlight when one is filtered. Trust signals are still relevant for Unknown files, so the
  // card stays visible and interactive regardless of the tag.
  const highlightsActive = !filters.tagId;
  const selectedStatus = highlightsActive ? filters.trustStatus || '' : '';
  const selectedRange = highlightsActive ? filters.lastSeenRange || '' : '';
  const selectedTrustSignal = filters.trustSignal || '';

  const isUnknownSelected = filters.trustStatus === UNMATCHED_FILE_STATUS.Unknown;
  // With no trust status selected the bars span every status; otherwise they follow the selection.
  const lastSeenStatusLabel = selectedStatus ? getUnmatchedFileStatusLabel(selectedStatus) : 'All';

  const statusData = useMemo(() => {
    const countByStatus = new Map(
      (summaryQuery.data?.statusCounts || []).map(item => [item.status, item.count]),
    );
    return Object.values(UNMATCHED_FILE_STATUS).map(status => ({
      status,
      label: getUnmatchedFileStatusLabel(status),
      value: countByStatus.get(status) || 0,
      color: getUnmatchedFileStatusColor(status),
    }));
  }, [summaryQuery.data]);

  const lastSeenData = useMemo(() => {
    const source = !selectedStatus
      ? summaryQuery.data?.allLastSeenRangeCounts
      : selectedStatus === UNMATCHED_FILE_STATUS.Trusted
        ? summaryQuery.data?.trustedLastSeenRangeCounts
        : selectedStatus === UNMATCHED_FILE_STATUS.Ignored
          ? summaryQuery.data?.ignoredLastSeenRangeCounts
          : selectedStatus === UNMATCHED_FILE_STATUS.Unknown
            ? summaryQuery.data?.unknownLastSeenRangeCounts
            : [];
    return buildRangeData(source);
  }, [summaryQuery.data, selectedStatus]);

  const trustSignalCounts = useMemo(
    () => summaryQuery.data?.trustSignalCounts || {},
    [summaryQuery.data],
  );

  // Conditional "Get Started" callouts are scoped to files seen in the last 30 days.
  const unknownCount = useMemo(
    () =>
      getRangeCount(
        starterSummaryQuery.data?.unknownLastSeenRangeCounts,
        UNMATCHED_FILE_LAST_SEEN_RANGE.Last30Days,
      ),
    [starterSummaryQuery.data],
  );
  const trustedRecentCount = useMemo(
    () =>
      getRangeCount(
        starterSummaryQuery.data?.trustedLastSeenRangeCounts,
        UNMATCHED_FILE_LAST_SEEN_RANGE.Last30Days,
      ),
    [starterSummaryQuery.data],
  );
  const starterTrustSignalCounts = useMemo(
    () => starterSummaryQuery.data?.trustSignalCounts || {},
    [starterSummaryQuery.data],
  );

  const handleStatusClick = useCallback(
    status => {
      if (!status) {
        return;
      }
      const next = filters.trustStatus === status ? '' : status;
      onFiltersChange({
        ...filters,
        trustStatus: next,
        // Trust signals only make sense for Unknown.
        trustSignal: next === UNMATCHED_FILE_STATUS.Unknown ? filters.trustSignal || '' : '',
      });
    },
    [filters, onFiltersChange],
  );

  const handleRangeClick = useCallback(
    item => {
      const range = item?.range || item?.payload?.range || item;
      if (!range) {
        return;
      }
      // Last audit/block is its own filter — toggle the range without forcing a trust status, so
      // it works whether or not a status is selected.
      onFiltersChange({
        ...filters,
        lastSeenRange: filters.lastSeenRange === range ? '' : range,
      });
    },
    [filters, onFiltersChange],
  );

  const handleTrustSignalClick = useCallback(
    signal => {
      const isActive = filters.trustSignal === signal;
      onFiltersChange({
        ...filters,
        trustStatus: UNMATCHED_FILE_STATUS.Unknown,
        trustSignal: isActive ? '' : signal,
      });
      if (!isActive) {
        if (onGroupByChange && TRUST_SIGNAL_TO_GROUP_BY[signal]) {
          onGroupByChange(TRUST_SIGNAL_TO_GROUP_BY[signal]);
        }
        // Surface the table that best fits the signal (signer-centric vs file-centric).
        if (onActiveTableChange && TRUST_SIGNAL_TO_TABLE[signal]) {
          onActiveTableChange(TRUST_SIGNAL_TO_TABLE[signal]);
        }
      }
    },
    [filters, onFiltersChange, onGroupByChange, onActiveTableChange],
  );

  const handleTrustedStarterClick = useCallback(() => {
    onFiltersChange({
      ...filters,
      trustStatus: UNMATCHED_FILE_STATUS.Trusted,
      lastSeenRange: UNMATCHED_FILE_LAST_SEEN_RANGE.Last30Days,
      trustSignal: '',
    });
    if (onActiveTableChange) {
      onActiveTableChange('files');
    }
  }, [filters, onFiltersChange, onActiveTableChange]);

  const handleUnknownStarterClick = useCallback(() => {
    applyUnknownStarterFilters(starterTrustSignalCounts);
  }, [applyUnknownStarterFilters, starterTrustSignalCounts]);

  return (
    <Stack gap={1.5} sx={{ width: '100%' }}>
      <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.4 }}>
        Unmatched Files Overview
      </Typography>

      {summaryQuery.isLoading ? (
        <SectionSkeleton />
      ) : (
        <>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={1.5}
            alignItems="stretch"
            sx={{ width: '100%' }}>
            <GetStartedCard
              onTrustedClick={handleTrustedStarterClick}
              onUnknownClick={handleUnknownStarterClick}
              onPendingClick={() => setCatalogModalOpen(true)}
              showUnknown={unknownCount > 0}
              showTrusted={trustedRecentCount > 0}
              showPending={unrepresentedFileCount > 0}
              unknownCount={unknownCount}
              trustedRecentCount={trustedRecentCount}
              pendingCatalogCount={unrepresentedFileCount}
            />
            <Stack gap={1.5} sx={{ flex: '1 1 0', minWidth: 320 }}>
              <UnmatchedFilesCatalogCard
                sx={{ flex: '1 1 auto' }}
                modalOpen={catalogModalOpen}
                onModalOpenChange={setCatalogModalOpen}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ ...CARD_SX }}>
                <TrustStatusPie
                  data={statusData}
                  selectedStatus={selectedStatus}
                  onStatusClick={handleStatusClick}
                />
                <LastSeenBars
                  statusLabel={lastSeenStatusLabel}
                  data={lastSeenData}
                  selectedRange={filters.trustStatus === selectedStatus ? selectedRange : ''}
                  onRangeClick={handleRangeClick}
                />
              </Stack>
            </Stack>
          </Stack>

          <Collapse in={isUnknownSelected} timeout="auto" unmountOnExit>
            <Stack gap={1.5}>
              <TrustSignalsCard
                counts={trustSignalCounts}
                selectedTrustSignal={selectedTrustSignal}
                onTrustSignalClick={handleTrustSignalClick}
              />
              {selectedTrustSignal ? <TrustSignalAlert signal={selectedTrustSignal} /> : null}
            </Stack>
          </Collapse>
        </>
      )}
    </Stack>
  );
};

export default UnmatchedFilesCharts;
